import { slugify } from './slugify';

/**
 * Extract all headings from Tiptap JSON content
 * @param {array} content - Tiptap JSON content array
 * @returns {array} - Array of headings with level, text, and slug
 */
export const extractHeadings = (content) => {
  const headings = [];

  const traverse = (nodes) => {
    if (!nodes || !Array.isArray(nodes)) return;

    nodes.forEach((node) => {
      if (node.type === 'heading' && node.attrs && node.content) {
        const text = extractTextFromContent(node.content);
        const slug = slugify(text);

        headings.push({
          level: node.attrs.level,
          text,
          slug,
          id: `heading-${slug}`, // For scrolling/positioning
        });
      }

      if (node.content && Array.isArray(node.content)) {
        traverse(node.content);
      }
    });
  };

  traverse(content);
  return headings;
};

/**
 * Extract plain text from Tiptap content array
 * @param {array} contentArray - Tiptap content nodes
 * @returns {string} - Plain text
 */
export const extractTextFromContent = (contentArray) => {
  if (!contentArray || !Array.isArray(contentArray)) return '';

  return contentArray
    .map((node) => {
      if (node.type === 'text') {
        return node.text || '';
      }
      return '';
    })
    .join('');
};

/**
 * Build sidebar structure from headings
 * H1 = main section
 * H2 = subsection
 * H3 = sub-subsection
 */
export const buildSidebarStructure = (headings) => {
  const structure = [];
  let currentSection = null;
  let currentSubsection = null;

  headings.forEach((heading) => {
    if (heading.level === 1) {
      currentSection = {
        ...heading,
        children: [],
      };
      structure.push(currentSection);
      currentSubsection = null;
    } else if (heading.level === 2 && currentSection) {
      currentSubsection = {
        ...heading,
        children: [],
      };
      currentSection.children.push(currentSubsection);
    } else if (heading.level === 3 && currentSubsection) {
      currentSubsection.children.push(heading);
    }
  });

  return structure;
};

/**
 * Find heading by slug
 * @param {string} slug - The slug to find
 * @param {array} headings - Array of headings
 * @returns {object|null} - Heading object or null
 */
export const findHeadingBySlug = (slug, headings) => {
  return headings.find((h) => h.slug === slug) || null;
};

/**
 * Get section heading (H1) by slug
 * Useful for finding which main section a URL belongs to
 */
export const getSectionHeadingBySlug = (slug, structure) => {
  for (const section of structure) {
    if (section.slug === slug) {
      return section;
    }
    for (const subsection of section.children) {
      if (subsection.slug === slug) {
        return section; // Return parent section
      }
      for (const subsubsection of subsection.children) {
        if (subsubsection.slug === slug) {
          return section; // Return parent section
        }
      }
    }
  }
  return null;
};
